import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { variants } from '@/animation/common-variants';
import type { BadgeOrigin } from '@/atoms/Badge/badgeposition';
import {
  anchorOriginBottomLeft,
  anchorOriginBottomRight,
  anchorOriginTopLeft,
  anchorOriginTopRight,
} from '@/atoms/Badge/badgeposition';
import getTestIDs from '@/utils/getTestIDs';

export type DotType = {
  /**
   * anchor Origin props for badge
   */
  anchorOrigin: BadgeOrigin;
  /**
   * The content rendered within the badge.
   */
  badgeContent?: React.ReactNode;
  /**
   * The color variants for the badge
   */
  color?: 'default' | 'error' | 'gray' | 'primary' | 'secondary';
  /**
   * If `true`, the badge will be invisible.
   */
  invisible?: boolean;
  /**
   * Max count to show.
   */
  max: number;
  /**
   * Controls whether the badge is hidden when `badgeContent` is zero.
   */
  showZero?: boolean;
  /**
   * Size of the badge
   */
  size?: 'medium' | 'small';
  /**
   * The type of the badge
   */
  variant: 'dot' | 'standard';
};

export const testIDs = getTestIDs();

const Position = styled.span<Pick<DotType, 'anchorOrigin'>>`
  ${tw`block absolute`}
  ${({ anchorOrigin }) =>
    `${anchorOrigin.vertical} ${anchorOrigin.horizontal}` === 'top right' &&
    `${anchorOriginTopRight}`}
  ${({ anchorOrigin }) =>
    `${anchorOrigin.vertical} ${anchorOrigin.horizontal}` === 'top left' &&
    `${anchorOriginTopLeft}`}
  ${({ anchorOrigin }) =>
    `${anchorOrigin.vertical} ${anchorOrigin.horizontal}` === 'bottom right' &&
    `${anchorOriginBottomRight}`}
  ${({ anchorOrigin }) =>
    `${anchorOrigin.vertical} ${anchorOrigin.horizontal}` === 'bottom left' &&
    `${anchorOriginBottomLeft}`}
`;

const StyledDotBadge = styled.span<Pick<DotType, 'color' | 'size' | 'variant'>>`
  ${tw`grid place-content-center text-white body-xs`}
  ${({ variant }) =>
    variant === 'dot' ? tw`h-2 w-2 p-0 rounded-full` : tw`p-1 rounded-full`}
  ${({ size, variant }) =>
    variant === 'standard'
      ? size === 'medium'
        ? tw`h-6 min-w-6`
        : tw`h-5 min-w-[1.25rem] body-xxs`
      : undefined}
  ${({ color }) => color === 'gray' && tw`bg-gray-900`}
  ${({ color }) => color === 'primary' && tw`bg-emerald-900`}
  ${({ color }) => color === 'secondary' && tw`bg-blue-900`}
  ${({ color }) => color === 'default' && tw`bg-yellow-100`}
  ${({ color }) => color === 'error' && tw`bg-secondary-red-1000`}
`;
const MotionIcon = motion(StyledDotBadge);

export const DotBadge = React.forwardRef<HTMLSpanElement, DotType>(
  (
    {
      anchorOrigin,
      badgeContent,
      color,
      max,
      invisible: invisibleProp,
      showZero,
      size = 'medium',
      variant,
      ...rest
    },
    ref
  ) => {
    let displayValue = null;
    let invisible = invisibleProp;

    const isNode = React.isValidElement(badgeContent);

    if (variant !== 'dot') {
      displayValue = !isNode
        ? badgeContent &&
          !isNaN(Number(badgeContent)) &&
          Number(badgeContent) > max
          ? `${max}+`
          : badgeContent
        : badgeContent;
    }
    if (
      invisibleProp == null &&
      ((badgeContent === 0 && !showZero) ||
        (badgeContent == null && variant !== 'dot'))
    ) {
      invisible = true;
    }

    return (
      <Position anchorOrigin={anchorOrigin}>
        <AnimatePresence initial={false}>
          {!invisible && (
            <MotionIcon
              {...rest}
              color={color}
              variant={variant}
              data-testid={
                variant === 'dot'
                  ? testIDs.notificationDot
                  : testIDs.notificationBadge
              }
              ref={ref}
              size={size}
              initial='initial'
              exit='exit'
              animate='animate'
              variants={variants['shrink']}
            >
              {displayValue}
            </MotionIcon>
          )}
        </AnimatePresence>
      </Position>
    );
  }
);
